import { useState } from "react";
import { Button, Chip, Grid, Paper, Stack, Typography, Box } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import {
  DateField,
  FunctionField,
  Show,
  SimpleShowLayout,
  TextField,
  useNotify,
  useRecordContext,
  useRefresh,
} from "react-admin";

import { API_URL } from "../../config";
import { adminApi } from "../../dataProvider";
import { PASSPORT_FIELDS, kycAnswerLabel, kycModeLabel, kycStatusLabel, passportValue } from "./labels";

function ReviewActions() {
  const record = useRecordContext<{ id: string; review_answer: string | null }>();
  const notify = useNotify();
  const refresh = useRefresh();
  const [busy, setBusy] = useState(false);
  if (!record) return null;

  const decide = async (action: "approve" | "reject") => {
    let comment: string | undefined;
    if (action === "reject") {
      comment = window.prompt("Причина отказа (необязательно):") ?? undefined;
    }
    setBusy(true);
    try {
      await adminApi(`/admin/kyc-applicants/${record.id}/${action}`, {
        method: "POST",
        json: action === "reject" ? { comment } : undefined,
      });
      notify(action === "approve" ? "KYC подтверждён — реквизиты выданы по заявкам" : "KYC отклонён", {
        type: "success",
      });
      refresh();
    } catch (e) {
      notify(e instanceof Error ? e.message : "Ошибка", { type: "error" });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Stack direction="row" spacing={2}>
      <Button
        variant="contained"
        color="success"
        startIcon={<CheckCircleIcon />}
        disabled={busy || record.review_answer === "GREEN"}
        onClick={() => decide("approve")}
      >
        Подтвердить KYC
      </Button>
      <Button
        variant="outlined"
        color="error"
        startIcon={<CancelIcon />}
        disabled={busy || record.review_answer === "RED"}
        onClick={() => decide("reject")}
      >
        Отклонить
      </Button>
    </Stack>
  );
}

type KycFile = { id: string; kind: string; mime: string; size: number; created_at?: string; url: string };

const KIND_TITLE: Record<string, string> = {
  passport_spread: "Разворот паспорта",
  registration: "Прописка",
  selfie_with_passport: "Селфи с паспортом",
};

function FilesGrid() {
  const record = useRecordContext<{ files?: KycFile[] }>();
  if (!record?.files?.length) return <Typography variant="body2">Файлов нет</Typography>;
  return (
    <Grid container spacing={2}>
      {record.files.map((f) => (
        <Grid item xs={12} md={4} key={f.id}>
          <Paper variant="outlined" sx={{ p: 1.5 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              {KIND_TITLE[f.kind] ?? f.kind}
            </Typography>
            <Box
              component="a"
              href={API_URL + f.url}
              target="_blank"
              rel="noreferrer"
              sx={{ display: "block" }}
            >
              <Box
                component="img"
                src={API_URL + f.url}
                alt={f.kind}
                sx={{ display: "block", width: "100%", maxHeight: 320, objectFit: "contain", bgcolor: "#000", borderRadius: 1 }}
              />
            </Box>
            <Typography variant="caption" sx={{ display: "block", mt: 0.5, color: "text.secondary" }}>
              {f.mime} · {Math.round(f.size / 1024)} KB
            </Typography>
          </Paper>
        </Grid>
      ))}
    </Grid>
  );
}

function ParsedPassport() {
  const record = useRecordContext<{ parsed_passport?: Record<string, unknown> | null; mode?: string }>();
  if (record?.mode !== "manual") return null;
  const data = record?.parsed_passport;
  if (!data) {
    return (
      <Typography variant="body2" color="text.secondary">
        Распознавание не запускалось.
      </Typography>
    );
  }
  if (data._error) {
    return (
      <Typography variant="body2" color="error">
        Не удалось распознать данные паспорта. Проверьте документ вручную.
      </Typography>
    );
  }
  return (
    <Stack spacing={1.25}>
      {PASSPORT_FIELDS.filter(({ key }) => data[key] != null && data[key] !== "").map(({ key, label }) => (
        <Stack key={key} direction="row" spacing={2}>
          <Typography variant="body2" sx={{ width: 180, flexShrink: 0, color: "text.secondary" }}>
            {label}
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {passportValue(key, data[key])}
          </Typography>
        </Stack>
      ))}
    </Stack>
  );
}

// Анкета клиента: страна резидентства и то, что он ввёл руками. У нерезидента документ
// не российского образца — авто-сверка фото не запускается, решение принимает оператор.
function ProfileForm() {
  const record = useRecordContext<{
    residency_country?: string | null;
    confirmed_profile?: Record<string, unknown> | null;
  }>();
  const country = record?.residency_country || "";
  const prof = record?.confirmed_profile;
  if (!country && !prof) {
    return (
      <Typography variant="body2" color="text.secondary">
        Анкета не заполнена.
      </Typography>
    );
  }
  const foreign = Boolean(country) && country !== "Россия";
  const rows: { label: string; value: string }[] = [
    { label: "Резидентство", value: foreign ? country : "Россия" },
    { label: "Фамилия", value: String(prof?.last_name ?? "") },
    { label: "Имя", value: String(prof?.first_name ?? "") },
    { label: "Отчество", value: String(prof?.middle_name ?? "") },
    { label: "Дата рождения", value: String(prof?.date_of_birth ?? "") },
    { label: "Серия", value: String(prof?.passport_series ?? "") },
    { label: "Номер", value: String(prof?.passport_number ?? "") },
    { label: "Дата выдачи", value: String(prof?.issued_at ?? "") },
    { label: "Код подразделения", value: String(prof?.division_code ?? "") },
  ].filter((r) => r.value !== "");

  return (
    <Stack spacing={1.25}>
      {foreign && (
        <Typography variant="body2" color="warning.main">
          Нерезидент РФ — документ иностранного образца, авто-проверка фото не выполнялась.
        </Typography>
      )}
      {rows.map((r) => (
        <Stack key={r.label} direction="row" spacing={2}>
          <Typography variant="body2" sx={{ width: 180, flexShrink: 0, color: "text.secondary" }}>
            {r.label}
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {r.value}
          </Typography>
        </Stack>
      ))}
    </Stack>
  );
}

export function KycShow() {
  return (
    <Show>
      <SimpleShowLayout>
        <TextField source="user_phone" label="Телефон" />
        <TextField source="external_user_id" label="ID клиента" />
        <FunctionField label="Режим" render={(r: { mode: string }) => <Chip size="small" label={kycModeLabel(r.mode)} />} />
        <FunctionField
          label="Статус / Решение"
          render={(r: { review_status: string | null; review_answer: string | null }) => (
            <Stack direction="row" spacing={1}>
              {r.review_status && <Chip size="small" label={kycStatusLabel(r.review_status)} />}
              {r.review_answer && (
                <Chip
                  size="small"
                  color={r.review_answer === "GREEN" ? "success" : "error"}
                  label={kycAnswerLabel(r.review_answer)}
                />
              )}
            </Stack>
          )}
        />
        <FunctionField label="Решение оператора" render={() => <ReviewActions />} />
        <TextField source="moderation_comment" label="Комментарий" />
        <TextField source="level_name" label="Уровень" />
        <DateField source="created_at" label="Создано" showTime />
        <DateField source="updated_at" label="Обновлено" showTime />

        <FunctionField label="Анкета клиента" render={() => <ProfileForm />} />
        <FunctionField label="Загруженные документы" render={() => <FilesGrid />} />
        <FunctionField label="Распознанные паспортные поля" render={() => <ParsedPassport />} />
      </SimpleShowLayout>
    </Show>
  );
}
